import { useRef, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material/styles';
import { mdiImage } from '@mdi/js';

import { Icon } from 'common/components/Icon';
import { useUploadFiles } from 'common/hooks/useUploadFiles';
import { IBasicFileFragment, IFileFragment } from 'graphql/types';
import { OverlayCircle } from 'common/components/OverlayCircle';
import { FilePicker } from 'common/components/FilePicker';
import { ImageCropperDialog } from 'common/components/ImageCropperDialog';
import { IMAGE_VARIANTS } from 'common/constants/image';
import { EImageVariant } from 'common/types';

const errorMessageStyle: SxProps = {
    width: '100%',
    height: 'auto',
    textAlign: 'center',
    fontSize: 16,
    color: 'error.main',
    position: 'absolute',
    top: '75%',
};

const imageUploaderStyle: SxProps<Theme> = (theme) => ({
    backgroundColor: 'grey.100',
    borderRadius: `${theme.shape.borderRadius}px`,
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '150px',
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: '300ms border-color',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',

    '&:hover': {
        borderColor: 'divider.main',
    },

    '&.noBackground': {
        backgroundColor: 'unset',
    },

    '.filePicker': {
        width: '100%',
        height: '100%',
    },
});

export interface IProps {
    'aria-labelledby'?: string;
    id: string;
    className?: string;
    description?: string;
    label?: string;
    image?: IBasicFileFragment;
    imageSize?: 'contain' | 'cover';
    maxUploadSize: number;
    /** Pass accepted file types as a comma separated string with accepted mime types. e.g. "image/png, image/jpeg" */
    acceptedFileTypes?: string;
    enableCropping?: boolean;
    imageVariant?: EImageVariant;
    onUpload?(file: IFileFragment): void;
    onUploadFinish?: () => void;
    onUploadStart?: () => void;
    onError?(message: string): void;
}

export const ImageUploader = ({
    'aria-labelledby': ariaLabelledby,
    className,
    id,
    image,
    imageSize,
    maxUploadSize,
    acceptedFileTypes,
    enableCropping = false,
    imageVariant,
    onUpload,
    onUploadFinish,
    onUploadStart,
}: IProps) => {
    const imageVariantData = imageVariant
        ? IMAGE_VARIANTS[imageVariant]
        : undefined;
    const aspectRatio = imageVariantData?.aspectRatio;

    const [cropperOpen, setCropperOpen] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const { isUploading, uploadFiles, uploadingFiles } = useUploadFiles({
        onSuccess: (_fileIndex, updatedFile) => {
            if (!updatedFile) return;

            onUpload && onUpload(updatedFile);
        },
    });

    const prevIsUploading = useRef(isUploading);

    useEffect(() => {
        const { current: prevUploading } = prevIsUploading;

        if (isUploading === prevUploading) return;

        prevIsUploading.current = isUploading;

        if (!isUploading && onUploadFinish) {
            return onUploadFinish();
        }

        onUploadStart && onUploadStart();
    }, [isUploading, onUploadFinish, onUploadStart]);

    const handleUpload = (files: File[]) => {
        if (enableCropping && files.length > 0) {
            setSelectedFile(files[0]);
            setCropperOpen(true);
        } else {
            uploadFiles(files, maxUploadSize);
        }
    };

    const handleCropConfirm = (croppedFile: File) => {
        setCropperOpen(false);
        setSelectedFile(null);
        uploadFiles([croppedFile], maxUploadSize);
    };

    const handleCropCancel = () => {
        setCropperOpen(false);
        setSelectedFile(null);
    };

    const uploadingFile = uploadingFiles.length ? uploadingFiles[0] : undefined;

    const {
        done: fileDone,
        success: fileSuccess,
        progress: fileProgress,
        errorMessage: fileErrorMessage,
    } = uploadingFile?.status || {};

    const { file } = uploadingFile || {};

    const progressValue =
        !!(uploadingFile && !fileDone && fileProgress && fileProgress >= 0) &&
        `${fileProgress}%`;

    const fileUploaded = uploadingFile && fileDone;
    const noFileUploading = !uploadingFile || fileUploaded;

    const errorMessage = fileUploaded && !fileSuccess && (
        <Box sx={errorMessageStyle}>{fileErrorMessage}</Box>
    );

    let fileUrl = image?.url || '';

    // If file is uploaded and not deleted by the user afterwards
    if (fileSuccess && image?.url) fileUrl = file?.url || '';

    return (
        <>
            <Box
                className={className}
                style={{ backgroundImage: `url(${fileUrl})` }}
                sx={(theme) => ({
                    ...imageUploaderStyle(theme),
                    backgroundSize:
                        !imageSize || imageSize === 'cover'
                            ? 'cover'
                            : imageSize,
                    backgroundOrigin:
                        !imageSize || imageSize === 'cover'
                            ? undefined
                            : 'content-box',
                    padding:
                        !imageSize || imageSize === 'cover'
                            ? undefined
                            : `${theme.spacing(2, 2)}`,
                })}
            >
                <FilePicker
                    aria-labelledby={ariaLabelledby}
                    className="filePicker"
                    id={id}
                    inputProps={{
                        accept: acceptedFileTypes,
                        'aria-labelledby': ariaLabelledby,
                    }}
                    onUpload={noFileUploading ? handleUpload : undefined}
                >
                    <OverlayCircle>
                        {progressValue ? (
                            progressValue
                        ) : (
                            <Icon path={mdiImage} size="3rem" />
                        )}
                    </OverlayCircle>
                </FilePicker>

                {errorMessage}
            </Box>

            {enableCropping && (
                <ImageCropperDialog
                    aspectRatio={aspectRatio}
                    imageFile={selectedFile}
                    minHeight={imageVariantData?.minHeight}
                    minWidth={imageVariantData?.minWidth}
                    open={cropperOpen}
                    onCancel={handleCropCancel}
                    onConfirm={handleCropConfirm}
                />
            )}
        </>
    );
};
